import React, { useEffect, useState } from 'react';
import useDataGrid from '@afosto/utils-react/datagrid/useDataGrid';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import { useHistory } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardActionArea from '@material-ui/core/CardActionArea';
import CardContent from '@material-ui/core/CardContent';
import Chip from '@material-ui/core/Chip';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import IconButton from '@material-ui/core/IconButton';
import MuiLink from '@material-ui/core/Link';
import Skeleton from '@material-ui/lab/Skeleton';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import CreateTransactionDrawer from '../../components/CreateTransactionDrawer';
import TransactionPreview from './components/TransactionPreview';
import DataGrid from '@afosto/app-components-react/DataGrid';
import DataList from '@afosto/app-components-react/DataList';
import Dialog from '@afosto/app-components-react/Dialog';
import Icon from '@afosto/app-components-react/Icon';
import Page from '@afosto/app-components-react/Page';
import Property from '@afosto/app-components-react/Property';
import dataSourceRequest from '@afosto/utils-react/dataSource/dataSourceRequest';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import formatCurrency from '@afosto/utils/formatCurrency';
import { transactions as transactionsApi } from '@afosto/order/api/Transactions';
import { transactionFormatter, clientFormatter, referenceFormatter } from './formatters';
import { datetime } from '@afosto/app-components-react/DataGrid/formatters';
import translations from './translations';

const TRANSACTION_APP_URL = 'https://transactions.afosto.io';

const TransactionContainer = props => <Grid container spacing={2} {...props} />;

const fetchTransactions = async dataSource =>
  dataSourceRequest(
    dataSource,
    async (data, requestOptions) => transactionsApi.listTransactions({}, requestOptions),
    {
      transformData: data =>
        data.reduce(
          (acc, transaction) => [
            ...acc,
            { ...transaction, entity_type: transaction?.reference?.entity_type },
          ],
          [],
        ),
    },
  );

const Transactions = () => {
  usePrivate();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['odr:providers:read']);
  const intl = useIntl();
  const history = useHistory();
  const [showPaymentLinkDialog, setShowPaymentLinkDialog] = useState(false);
  const [transaction, setTransaction] = useState(null);
  const [isFetchingTransaction, setIsFetchingTransaction] = useState(false);
  const [showCreateTransaction, setShowCreateTransaction] = useState(false);
  const [openTooltipID, setOpenTooltipID] = useState(null);

  const dataSource = useDataGrid(fetchTransactions, {
    modules: ['dataProperties', 'pagination', 'activeItem'],
    dataProperties: {
      fixedProperties: [
        {
          key: 'number',
          name: intl.formatMessage({
            id: 'dataGrid.columns.transactions.number',
            defaultMessage: 'Number',
            description: 'The column name for column number',
          }),
          type: 'transaction',
        },
        {
          key: 'description',
          name: intl.formatMessage({
            id: 'dataGrid.columns.transactions.description',
            defaultMessage: 'Description',
            description: 'The column name for column description',
          }),
        },
        {
          key: 'entity_type',
          name: intl.formatMessage({
            id: 'dataGrid.columns.transactions.entityType',
            defaultMessage: 'Type',
            description: 'The column name for column entity_type',
          }),
          type: 'translation',
          formatOptions: {
            translationKey: 'properties.{value}',
          },
        },
        {
          key: 'amount_due',
          name: intl.formatMessage({
            id: 'dataGrid.columns.transactions.amountDue',
            defaultMessage: 'Amount due',
            description: 'The column name for column amount due',
          }),
          type: 'currency',
        },
        {
          key: 'amount_paid',
          name: intl.formatMessage({
            id: 'dataGrid.columns.transactions.amountPaid',
            defaultMessage: 'Amount paid',
            description: 'The column name for column amount paid',
          }),
          type: 'currency',
        },
        {
          key: 'is_paid',
          name: intl.formatMessage({
            id: 'dataGrid.columns.transactions.isPaid',
            defaultMessage: 'Paid',
            description: 'The column name for column is paid',
          }),
          type: 'boolean',
        },
        {
          key: 'client_id',
          name: intl.formatMessage({
            id: 'dataGrid.columns.transactions.client',
            defaultMessage: 'Client',
            description: 'The column name for column client',
          }),
          type: 'client',
        },
        {
          key: 'reference',
          name: intl.formatMessage({
            id: 'dataGrid.columns.transactions.reference',
            defaultMessage: 'Reference',
            description: 'The column name for column reference',
          }),
          type: 'reference',
        },
        {
          key: 'created_at',
          name: intl.formatMessage({
            id: 'dataGrid.columns.transactions.createdAt',
            defaultMessage: 'Created at',
            description: 'The column name for column created at',
          }),
          type: 'datetime',
        },
      ],
    },
  });
  const { data: dataSourceData = [], activeItem: activeItemModule = {} } = dataSource;
  const { activeItem } = activeItemModule;

  /**
   * Close the create transaction drawer.
   */
  const handleCloseCreateTransaction = () => {
    setShowCreateTransaction(false);
  };

  /**
   * Close the transaction preview
   */
  const handleCloseTransactionPreview = () => {
    dataSource.activeItem.setActiveItem(null);
    setTransaction(null);
  };

  /**
   * Handler that fires after creating the transaction.
   */
  const handleCreateTransaction = () => {
    setShowCreateTransaction(false);
  };

  /**
   * Handler that fires after saving the transaction.
   * @param {object} savedTransaction
   */
  const handleSaveTransaction = savedTransaction => {
    setTransaction(savedTransaction);
  };

  const previewMenu = [
    ...(transaction
      ? [
          {
            key: 'view_transaction',
            translationKey: 'actions.view',
            label: 'View',
            icon: 'eye',
            shortcut: true,
            onClick: () => history.push(`/transactions/${transaction?.secret}`),
          },
        ]
      : []),
    ...(transaction?.amount_due > 0
      ? [
          {
            key: 'open_payment_link',
            translationKey: 'pages.transactions.preview.openPayment',
            label: 'Start payment',
            onClick: () => setShowPaymentLinkDialog(true),
          },
        ]
      : []),
  ];

  /**
   * Fetch the transaction data if a transaction row is selected and show the data in the preview.
   */
  useEffect(() => {
    let isMounted = true;
    const activeTransaction = findObjectInArray(dataSourceData, 'id', activeItem);

    const fetchTransaction = async secret => {
      setIsFetchingTransaction(true);

      try {
        const response = await transactionsApi.getTransaction(secret);
        const { data = {} } = response;

        if (isMounted) {
          setTransaction(data);
        }
      } catch (err) {
        if (isMounted) {
          setTransaction(null);
        }
      } finally {
        if (isMounted) {
          setIsFetchingTransaction(false);
        }
      }
    };

    if (activeTransaction) {
      fetchTransaction(activeTransaction.secret);
    } else {
      setTransaction(null);
    }

    return () => {
      isMounted = false;
    };
  }, [activeItem, dataSourceData]);

  const renderTransaction = transactionData => (
    <Grid item xs={12} key={transactionData?.id}>
      <Card>
        <CardActionArea onClick={() => history.push(`/transactions/${transactionData?.secret}`)}>
          <CardContent>
            <Box display="flex" justifyContent="space-between">
              <div>
                <Typography variant="subtitle1">
                  {transactionData?.number ?? transactionData?.id}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {transactionData?.description}
                </Typography>
              </div>
              <Box display="flex" flexDirection="column" alignItems="flex-end" mb={1}>
                <Typography variant="caption" color="textSecondary" style={{ marginBottom: 8 }}>
                  {datetime(transactionData?.created_at)}
                </Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  color={transactionData?.is_paid ? 'default' : 'secondary'}
                  label={
                    transactionData?.is_paid
                      ? intl.formatMessage(translations.transactionCard.status.paid)
                      : intl.formatMessage(translations.transactionCard.status.unpaid)
                  }
                />
              </Box>
            </Box>
            <Grid container spacing={4} justify="flex-end">
              <Grid item xs={4}>
                <Property
                  label={intl.formatMessage(translations.transactionCard.properties.total.label)}
                  value={formatCurrency(transactionData?.amount_due / 100)}
                  labelTypographyProps={{ align: 'right', component: 'div' }}
                  valueTypographyProps={{ align: 'right' }}
                />
              </Grid>
              <Grid item xs={4}>
                <Property
                  label={intl.formatMessage(translations.transactionCard.properties.paid.label)}
                  value={formatCurrency(transactionData?.amount_paid / 100)}
                  labelTypographyProps={{ align: 'right', component: 'div' }}
                  valueTypographyProps={{ align: 'right' }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </CardActionArea>
      </Card>
    </Grid>
  );

  const renderTransactionSkeleton = key => (
    <Grid item xs={12} key={key}>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between">
            <div>
              <Typography variant="subtitle1">
                <Skeleton variant="text" width={150} />
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <Skeleton variant="text" width={75} />
              </Typography>
            </div>
          </Box>
          <Grid container spacing={4} justify="flex-end">
            <Grid item xs={4}>
              <Property
                label={intl.formatMessage(translations.transactionCard.properties.total.label)}
                value={<Skeleton variant="text" width={75} />}
                labelTypographyProps={{ align: 'right', component: 'div' }}
                valueTypographyProps={{ align: 'right' }}
              />
            </Grid>
            <Grid item xs={4}>
              <Property
                label={intl.formatMessage(translations.transactionCard.properties.paid.label)}
                value={<Skeleton variant="text" width={75} />}
                labelTypographyProps={{ align: 'right', component: 'div' }}
                valueTypographyProps={{ align: 'right' }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Grid>
  );

  if (!hasPermissions()) {
    return (
      <Page title={intl.formatMessage(translations.title)} fullWidth>
        <UnauthorizedComponent />
      </Page>
    );
  }

  return (
    <>
      <Grid container wrap="nowrap">
        <Page title={intl.formatMessage(translations.title)} fullWidth style={{ flexGrow: 1 }}>
          <Hidden implementation="js" mdUp>
            <DataList
              dataSource={dataSource}
              renderItem={renderTransaction}
              renderSkeletonItem={renderTransactionSkeleton}
              component={TransactionContainer}
            />
          </Hidden>
          <Hidden implementation="js" smDown>
            <DataGrid
              dataSource={dataSource}
              formatters={{
                transaction: transactionFormatter,
                client: clientFormatter,
                reference: referenceFormatter,
              }}
            />
          </Hidden>
        </Page>
        <TransactionPreview
          isOpen={dataSource.activeItem.activeItem !== null}
          isLoading={isFetchingTransaction}
          transaction={transaction}
          onClose={handleCloseTransactionPreview}
          onSaveTransaction={handleSaveTransaction}
          previewMenu={previewMenu}
        />
        <CreateTransactionDrawer
          isOpen={showCreateTransaction}
          onClose={handleCloseCreateTransaction}
          onCreate={handleCreateTransaction}
        />
      </Grid>
      <Dialog open={showPaymentLinkDialog} onClose={() => setShowPaymentLinkDialog(false)}>
        <DialogTitle>{intl.formatMessage(translations.paymentLinkDialog.title)}</DialogTitle>
        <DialogContent>
          <Box display="flex" alignItems="center" flex={1}>
            <MuiLink
              variant="subtitle1"
              href={`${TRANSACTION_APP_URL}/${transaction?.secret}`}
              target="_blank"
              style={{ wordBreak: 'break-all' }}
            >
              https://transactions.afosto.io/{transaction?.secret}
            </MuiLink>
            {transaction && navigator?.clipboard?.writeText && (
              <ClickAwayListener onClickAway={() => setOpenTooltipID(null)}>
                <div>
                  <Tooltip
                    PopperProps={{
                      disablePortal: true,
                    }}
                    onClose={() => setOpenTooltipID(null)}
                    open={openTooltipID === 'paymentLink'}
                    disableFocusListener
                    disableHoverListener
                    disableTouchListener
                    title={intl.formatMessage(
                      translations.paymentLinkDialog.fields.paymentLinkUrl.tooltip,
                    )}
                  >
                    <IconButton
                      edge="end"
                      aria-label={intl.formatMessage(
                        translations.paymentLinkDialog.fields.paymentLinkUrl.button,
                      )}
                      onClick={() => {
                        navigator.clipboard.writeText(
                          `${TRANSACTION_APP_URL}/${transaction?.secret}`,
                        );
                        setOpenTooltipID('paymentLink');
                      }}
                    >
                      <Icon icon={['fal', 'copy']} />
                    </IconButton>
                  </Tooltip>
                </div>
              </ClickAwayListener>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPaymentLinkDialog(false)} color="secondary">
            {intl.formatMessage(translations.paymentLinkDialog.actions.close)}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Transactions;
